import { DurationType } from 'dg-web-shared/dto/PermitTimeUnit.ts';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { isDefined, Maybe, thenElse } from 'dg-web-shared/lib/MaybeV2.ts';
import { numberToPrice } from 'dg-web-shared/lib/NumberFormatter.ts';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Translation } from '../../common/i18n/Text.ts';
import * as OperatorDataState from '../../common/state/OperatorDataState.ts';
import * as PermitTypeState from '../../common/state/PermitTypeState.ts';
import {
    getIdentificationTypesGivenSelection,
    hasBadgeIdentification,
    hasLicensePlateIdentification,
} from '../../common/state/PermitTypeState.ts';
import * as SettingsState from '../../common/state/SettingsState.ts';
import { IconButton24pxType } from '../../ui/buttons/IconButton.tsx';
import {
    MiniTable,
    MiniTableActionOnlyElement,
    MiniTableElement,
} from '../../ui/labeled-elements/MiniTable.tsx';
import { TextField } from '../../ui/labeled-elements/TextField.tsx';
import { texts } from '../i18n/SharedTexts.ts';
import * as PermitCreateState from '../state/PermitCreateState.ts';
import * as PermitEditState from '../state/PermitEditState.ts';
import { Badge } from './AddBadgeSlideIn.tsx';
import { LicensePlate } from './AddLicensePlateSlideIn.tsx';
import { LicensePlateItem } from './LicensePlateItem.tsx';
import { validityIntervalTexts } from 'dg-web-shared/common/components/translations/PermitValidityIntervalTexts.ts';

export interface Texts {
    RemoveLpTooltip: Translation;
    AddLpTooltip: Translation;
    LicensePlates: Translation;
    RemoveBadgeTooltip: Translation;
    AddBadgeTooltip: Translation;
    Badges: Translation;
    AdditionalInfos: Translation;
    Remark: Translation;
    LicensePlateMissing: Translation;
    BadgeMissing: Translation;
}

interface IdentificationProps {
    canBeChanged: boolean;
    maxIdentificationCount: number;
    onIdentificationClick: (id: number) => void;
    onIdentificationAction: (id: number) => void;
    onAdd: () => void;
    showError: boolean;
}

interface LicensePlatesProps extends IdentificationProps {
    lps: LicensePlate[];
}

interface State {
    settings: SettingsState.State;
    operator: OperatorDataState.State;
    permitTypes: PermitTypeState.State;
    config: PermitCreateState.Config.State;
    permitEditServer: PermitEditState.Server.State;
    layout: PermitCreateState.Layout.State;
}

const getActivePermitTypeId = (s: State): Maybe<number> =>
    isDefined(s.permitEditServer.data) && !s.layout.createEnabled
        ? thenElse(s.permitEditServer.data, d => d.permitTypeId, null)
        : thenElse(s.config.permitTypeId, ptid => ptid, null);

export const renderPrice = (
    p: {
        price: number;
        priceUnit: DurationType | null;
    },
    lang: string,
): string => {
    const priceStr = numberToPrice(p.price);
    if (p.priceUnit && p.price > 0) {
        return `${priceStr} / ${getUnitStr(lang, p.priceUnit)}`;
    } else {
        return priceStr;
    }
};

function getUnitStr(lang: string, unit: string): string {
    switch (unit) {
        case 'day':
            return validityIntervalTexts[lang].Days(1);
        case 'month':
            return validityIntervalTexts[lang].Months(1);
        case 'year':
            return validityIntervalTexts[lang].Years(1);
        default:
            throw Error('Invalid price unit');
    }
}

function useSharedState(): State {
    const { storeState } = useStore(store => ({
        settings: new SettingsState.StateSlice(store).state,
        operator: OperatorDataState.get(store),
        permitTypes: PermitTypeState.get(store),
        config: PermitCreateState.Config.get(store),
        permitEditServer: PermitEditState.Server.get(store),
        layout: PermitCreateState.Layout.get(store),
    }));
    return storeState;
}

export function LicensePlates(props: LicensePlatesProps) {
    const state = useSharedState();

    if (
        !hasLicensePlateIdentification(
            getIdentificationTypesGivenSelection(
                state.permitTypes.data,
                thenElse(getActivePermitTypeId(state), id => [id], []),
            ),
        )
    ) {
        return null;
    }
    const lps = props.lps.map(lp => {
        return (
            <MiniTableElement
                key={lp.id}
                onLineItemClick={
                    props.canBeChanged
                        ? () => {
                              props.onIdentificationClick(lp.id);
                          }
                        : undefined
                }
                actionButton={
                    props.canBeChanged ? IconButton24pxType.clear : undefined
                }
                onAction={
                    props.canBeChanged
                        ? () => props.onIdentificationAction(lp.id)
                        : undefined
                }
                actionTooltip={texts[state.settings.language].RemoveLpTooltip()}
            >
                <LicensePlateItem lp={lp} />
            </MiniTableElement>
        );
    });
    if (lps.length < props.maxIdentificationCount && props.canBeChanged) {
        lps.push(
            <MiniTableActionOnlyElement
                key="add"
                actionButton={IconButton24pxType.add}
                actionTooltip={texts[state.settings.language].AddLpTooltip()}
                showUnderline={lps.length === 0}
                onAction={props.canBeChanged ? props.onAdd : undefined}
                errorText={
                    props.showError && lps.length === 0
                        ? texts[state.settings.language].LicensePlateMissing()
                        : undefined
                }
            />,
        );
    }
    return (
        <MiniTable
            label={texts[state.settings.language].LicensePlates()}
            labelRightText={`${props.lps.length}/${props.maxIdentificationCount}`}
            actionButtons={props.canBeChanged}
        >
            {lps}
        </MiniTable>
    );
}

interface BadgesProps extends IdentificationProps {
    badges: Badge[];
}

export function Badges(props: BadgesProps) {
    const state = useSharedState();

    if (
        !hasBadgeIdentification(
            getIdentificationTypesGivenSelection(
                state.permitTypes.data,
                thenElse(getActivePermitTypeId(state), id => [id], []),
            ),
        )
    ) {
        return null;
    }
    const badges = props.badges.map(badge => {
        return (
            <MiniTableElement
                key={badge.id}
                actionButton={
                    props.canBeChanged ? IconButton24pxType.clear : undefined
                }
                onAction={
                    props.canBeChanged
                        ? () => props.onIdentificationAction(badge.id)
                        : undefined
                }
                actionTooltip={texts[
                    state.settings.language
                ].RemoveBadgeTooltip()}
            >
                <div className="PermitDetailBadgeItem">
                    {badge.badgeLabelNr}
                </div>
            </MiniTableElement>
        );
    });

    if (badges.length < props.maxIdentificationCount && props.canBeChanged) {
        badges.push(
            <MiniTableActionOnlyElement
                key="add"
                actionButton={IconButton24pxType.add}
                actionTooltip={texts[state.settings.language].AddBadgeTooltip()}
                showUnderline={badges.length === 0}
                onAction={props.canBeChanged ? props.onAdd : undefined}
                errorText={
                    props.showError && badges.length === 0
                        ? texts[state.settings.language].BadgeMissing()
                        : undefined
                }
            />,
        );
    }

    return (
        <MiniTable
            label={texts[state.settings.language].Badges()}
            labelRightText={`${props.badges.length}/${props.maxIdentificationCount}`}
            actionButtons={props.canBeChanged}
        >
            {badges}
        </MiniTable>
    );
}

interface InfoProps {
    value: string;
    onChange: (v: string) => void;
    disabled: boolean;
}

export function AdditionalInfos(props: InfoProps) {
    const state = useSharedState();

    return (
        <TextField
            value={props.value}
            label={texts[state.settings.language].AdditionalInfos()}
            onChange={!props.disabled ? props.onChange : () => null}
            multiline={true}
            inputType="text"
            disabled={props.disabled}
        />
    );
}

export function Remark(props: InfoProps) {
    const state = useSharedState();

    return (
        <TextField
            value={props.value}
            multiline={true}
            label={texts[state.settings.language].Remark()}
            onChange={
                !props.disabled
                    ? props.onChange
                    : () => {
                          /*noop*/
                      }
            }
            inputType="text"
            disabled={props.disabled}
            toolTip={remarkTooltip}
        />
    );
}

interface EmailProps {
    value: string | null;
    onChange: (v: string) => void;
    error: Message | null;
}

export const Email = (p: EmailProps) => {
    return (
        <TextField
            value={p.value || ''}
            multiline={false}
            label={'E-Mail'}
            onChange={p.onChange}
            inputType="text"
            disabled={false}
            errorText={p.error ? <Localized {...p.error} /> : null}
        />
    );
};

const remarkTooltip = (
    <Localized
        de="Die interne Bemerkungen sind nur hier im Parkingportal sichtbar."
        fr="Les remarques internes sont uniquement visibles ici dans Parkingportal."
        it={
            'Le osservazioni interne sono visibili unicamente qui in Parkingportal.'
        }
        en="The internal comments are only visible here in Parkingportal."
    />
);
